import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { readVacancy, type Vacancy } from "../../lib/api/vacancies";
import { downloadBuffer } from "../../lib/browser/download";
import {
  type Workflow,
  type WorkflowGapQuestion,
  readAllWorkflows,
  writeWorkflow,
} from "../../lib/api/workflows";
import {
  type ConfirmedSkill,
  type ConfirmedGap,
  readProfileJson,
  writeProfileJson,
  listAllCandidates,
  readBestCvText,
} from "../../lib/api/profile";
import { analyzeVacancyMatch } from "../../lib/ai/vacancy-analysis";
import { generateDocument, type TailoredCVContent, type MotivatiebriefContent } from "../../lib/ai/document-gen";
import { formatCV } from "../../lib/documents/cv-formatter";
import { formatLetter } from "../../lib/documents/letter-formatter";
import {
  processEnrichmentAnswers,
  applyProfileUpdates,
  parseEmailResponseWithAI,
  type AnswerInput,
  type OpenWorkflowInput,
} from "../../lib/ai/enrichment";

export default function WorkflowWorkspace() {
  const t = useT();
  const repoPath = useAppStore((s) => s.repoPath);
  const activeCandidateFolder = useAppStore((s) => s.activeCandidateFolder);
  const activeWorkflowId = useAppStore((s) => s.activeWorkflowId);
  const [, navigate] = useLocation();
  const user = useAppStore((s) => s.user);
  const settings = useAppStore((s) => s.settings);
  const language = useAppStore((s) => s.language);

  // States
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [emailTextToParse, setEmailTextToParse] = useState("");
  const [parsingEmail, setParsingEmail] = useState(false);
  const [parseEmailError, setParseEmailError] = useState<string | null>(null);
  const [parseEmailSuccess, setParseEmailSuccess] = useState<string | null>(null);
  const [emailDraftStatus, setEmailDraftStatus] = useState<"idle" | "copied">("idle");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // AI analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<"overview" | "enrichment" | "documents">("overview");

  // Gap questions states
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [resolution, setResolution] = useState<"confirmed" | "gap">("confirmed");
  const [yearsOfExp, setYearsOfExp] = useState(1);
  const [savingAnswerId, setSavingAnswerId] = useState<string | null>(null);

  // Recalculation recommendation notice state
  const [showRecalcNotice, setShowRecalcNotice] = useState(false);

  // AI Enrichment state
  const [processingEnrichment, setProcessingEnrichment] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);
  const [enrichmentSuccess, setEnrichmentSuccess] = useState<string | null>(null);

  // Document states
  const [generatingDoc, setGeneratingDoc] = useState<"tailored_cv" | "motivatiebrief" | null>(null);
  const [docGenError, setDocGenError] = useState<string | null>(null);
  const [loadedCvContent, setLoadedCvContent] = useState<TailoredCVContent | null>(null);
  const [loadedLetterContent, setLoadedLetterContent] = useState<MotivatiebriefContent | null>(null);
  const [viewerTab, setViewerTab] = useState<"cv" | "letter">("cv");

  const getBackLabel = () => t("workspace.back");
  const goBack = () => {
    // Preserves browser history — if the workflow was opened from another
    // screen in this tab, this returns there; otherwise we fall home.
    if (window.history.length > 1) window.history.back();
    else navigate("/");
  };

  // Load all necessary files
  useEffect(() => {
    if (!repoPath || !activeCandidateFolder || !activeWorkflowId) return;

    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Get Candidate Name & Email
        const list = await listAllCandidates(repoPath);
        const cand = list.find((c) => c.folder === activeCandidateFolder);
        if (active && cand) {
          setCandidateName(cand.name);
        }
        const candidateProfile = await readProfileJson(repoPath, activeCandidateFolder);
        if (active && candidateProfile) {
          setCandidateEmail(candidateProfile.personal?.email || "");
        }

        // 2. Load Workflows
        const allWfs = await readAllWorkflows(repoPath, activeCandidateFolder);
        const currentWf = allWfs.find((w) => w.id === activeWorkflowId);
        if (!currentWf) {
          throw new Error("Workflow not found");
        }

        if (active) {
          setWorkflow(currentWf);
          
          // v2.0: generated document content lives inline in
          // workflow.documents.{tailored_cv|motivatiebrief}.content
          // (blob storage integration pending). Older records with only a
          // content_path won't have content available client-side.
          const cvDoc: any = currentWf.documents?.tailored_cv;
          if (cvDoc?.content) {
            setLoadedCvContent(cvDoc.content);
          } else {
            setLoadedCvContent(null);
          }

          const letterDoc: any = currentWf.documents?.motivatiebrief;
          if (letterDoc?.content) {
            setLoadedLetterContent(letterDoc.content);
          } else {
            setLoadedLetterContent(null);
          }
        }

        // 3. Load Vacancy via the API
        const parsedVac = await readVacancy(repoPath, currentWf.vacancy_id);
        if (!parsedVac) {
          throw new Error("Vacancy not found");
        }

        if (active) {
          setVacancy(parsedVac);
        }
      } catch (err: any) {
        console.error("Failed to load workflow workspace data", err);
        if (active) {
          setError(err?.message || "Error loading workspace data");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [repoPath, activeCandidateFolder, activeWorkflowId]);

  // Trigger AI Matching if missing on load
  useEffect(() => {
    if (loading || !workflow || !vacancy || analyzing) return;

    // Check if we need to auto-trigger the match evaluation
    if (!workflow.match_score) {
      void runAIVacancyAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, workflow, vacancy]);

  // Merges a fresh question list into existing ones, preserving answered history
  // and appending only questions for requirements not already covered.
  function mergeGapQuestions(
    existing: WorkflowGapQuestion[],
    fresh: WorkflowGapQuestion[],
  ): { merged: WorkflowGapQuestion[]; newCount: number } {
    const coveredRequirements = new Set(
      existing.map((q) => q.requirement.toLowerCase().trim()),
    );
    const genuinelyNew = fresh.filter(
      (q) => !coveredRequirements.has(q.requirement.toLowerCase().trim()),
    );
    return {
      merged: [
        ...existing,
        ...genuinelyNew.map((q) => ({ ...q, status: "unanswered" as const })),
      ],
      newCount: genuinelyNew.length,
    };
  }

  // mergeWith: when set, preserve existing questions and only append new ones.
  // Returns the number of new questions surfaced (0 when not merging).
  async function runAIVacancyAnalysis(
    mergeWith?: WorkflowGapQuestion[],
  ): Promise<number> {
    if (!repoPath || !activeCandidateFolder || !workflow || !vacancy) return 0;

    setAnalyzing(true);
    setAnalysisError(null);

    try {
      const apiKey = settings?.anthropic_api_key || "";
      const model = settings?.anthropic_model || "gemini-2.5-flash";

      if (!apiKey) {
        throw new Error(
          language === "nl"
            ? "AI API-sleutel ontbreekt in Instellingen. Voeg een API-sleutel toe om de matching te starten."
            : "AI API Key is missing in Settings. Please add an API Key to enable matching analysis."
        );
      }

      const cvText = await readBestCvText(repoPath, activeCandidateFolder);
      if (!cvText || cvText.trim().length === 0) {
        throw new Error(
          language === "nl"
            ? "Master-CV niet gevonden. Zorg ervoor dat het Master-CV is geüpload."
            : "Master CV not found. Please upload the Master CV first."
        );
      }

      const result = await analyzeVacancyMatch(
        apiKey,
        model,
        cvText,
        JSON.stringify(vacancy),
        settings?.prompts?.vacancy_analysis,
      );

      let finalQuestions: WorkflowGapQuestion[];
      let newCount = 0;

      if (mergeWith && mergeWith.length > 0) {
        // Post-enrichment re-analysis: keep history, append only new questions
        const freshMapped: WorkflowGapQuestion[] = result.gap_questions.map((q) => ({
          ...q,
          status: "unanswered" as const,
        }));
        const merged = mergeGapQuestions(mergeWith, freshMapped);
        finalQuestions = merged.merged;
        newCount = merged.newCount;
      } else {
        finalQuestions = result.gap_questions.map((q) => ({
          ...q,
          status: "unanswered" as const,
        }));
      }

      const updatedWf: Workflow = {
        ...workflow,
        match_score: result.match_score,
        match_rationale: result.match_rationale,
        confirmed_requirements: result.confirmed_requirements,
        gap_questions: finalQuestions,
        status: finalQuestions.some((q) => q.status === "unanswered")
          ? "questions_pending"
          : "pack_in_progress",
        updated_at: new Date().toISOString(),
      };

      await writeWorkflow(repoPath, activeCandidateFolder, vacancy.id, updatedWf);
      setWorkflow(updatedWf);
      setShowRecalcNotice(false);
      return newCount;
    } catch (err: any) {
      console.error("AI Matching failed", err);
      setAnalysisError(err?.message || "AI Analysis failed");
      return 0;
    } finally {
      setAnalyzing(false);
    }
  }

  // Save answer to gap question
  async function handleSaveAnswer(qId: string) {
    if (!repoPath || !activeCandidateFolder || !workflow || !vacancy) return;

    setSavingAnswerId(qId);
    try {
      // 1. Update the Workflow gap question
      const updatedQuestions = (workflow.gap_questions || []).map((q) => {
        if (q.id === qId) {
          return {
            ...q,
            status: "answered" as const,
            answer: answerText,
            answered_at: new Date().toISOString(),
            resolved_as: resolution,
          };
        }
        return q;
      });

      // Check if all questions are now answered
      const allAnswered = updatedQuestions.every((q) => q.status === "answered");
      const nextStatus = allAnswered ? "pack_in_progress" : "questions_pending";

      const updatedWf: Workflow = {
        ...workflow,
        gap_questions: updatedQuestions,
        status: nextStatus as any,
        updated_at: new Date().toISOString(),
      };

      // 2. Load candidate profile.json
      const profile = await readProfileJson(repoPath, activeCandidateFolder);
      if (profile) {
        const targetQuestion = workflow.gap_questions?.find((q) => q.id === qId);
        const reqText = targetQuestion?.requirement || "Requirement";

        if (resolution === "confirmed") {
          // Add to confirmed skills
          const newSkill: ConfirmedSkill = {
            skill: reqText,
            years: yearsOfExp,
            evidence: answerText,
            source: `Workflow for ${vacancy.title} (${vacancy.client})`,
            confirmed_at: new Date().toISOString(),
          };
          
          // Remove from gaps if it was there
          const filteredGaps = (profile.confirmed_gaps || []).filter(
            (g) => g.skill.toLowerCase() !== reqText.toLowerCase()
          );

          // Update skills (prevent exact duplicates by skill name)
          const filteredSkills = (profile.confirmed_skills || []).filter(
            (s) => s.skill.toLowerCase() !== reqText.toLowerCase()
          );

          profile.confirmed_skills = [...filteredSkills, newSkill];
          profile.confirmed_gaps = filteredGaps;
        } else {
          // Add to confirmed gaps
          const newGap: ConfirmedGap = {
            skill: reqText,
            notes: answerText,
            confirmed_at: new Date().toISOString(),
          };

          // Remove from skills if it was there
          const filteredSkills = (profile.confirmed_skills || []).filter(
            (s) => s.skill.toLowerCase() !== reqText.toLowerCase()
          );

          // Update gaps
          const filteredGaps = (profile.confirmed_gaps || []).filter(
            (g) => g.skill.toLowerCase() !== reqText.toLowerCase()
          );

          profile.confirmed_gaps = [...filteredGaps, newGap];
          profile.confirmed_skills = filteredSkills;
        }

        // Save updated profile
        await writeProfileJson(repoPath, activeCandidateFolder, profile);
      }

      // Write updated workflow
      await writeWorkflow(repoPath, activeCandidateFolder, vacancy.id, updatedWf);
      
      setWorkflow(updatedWf);
      setEditingQuestionId(null);
      setAnswerText("");
      setShowRecalcNotice(true); // Flag that match score could be recalculated
    } catch (err) {
      console.error("Failed to save gap answer", err);
    } finally {
      setSavingAnswerId(null);
    }
  }

  // AI enrichment: process all answered questions in one batch call
  async function handleAIEnrichment() {
    if (!repoPath || !activeCandidateFolder || !workflow || !vacancy) return;

    const answeredQuestions = (workflow.gap_questions || []).filter(
      (q) => q.status === "answered" && q.answer,
    );
    if (answeredQuestions.length === 0) return;

    const apiKey = settings?.anthropic_api_key || "";
    const model = settings?.anthropic_model || "gemini-2.5-flash";

    if (!apiKey) {
      setEnrichmentError(
        language === "nl"
          ? "AI API-sleutel ontbreekt in Instellingen."
          : "AI API Key is missing in Settings.",
      );
      return;
    }

    setProcessingEnrichment(true);
    setEnrichmentError(null);
    setEnrichmentSuccess(null);

    try {
      // 1. Read current profile
      const profile = await readProfileJson(repoPath, activeCandidateFolder);
      if (!profile) throw new Error("Profile not found — upload Master CV first.");

      // 2. Build answer inputs for AI
      const answers: AnswerInput[] = answeredQuestions.map((q) => ({
        question_id: q.id,
        requirement: q.requirement,
        classification: q.classification,
        question_en: q.question_en,
        answer_text: q.answer!,
      }));

      // 3. Open workflow context for re-scoring
      const openWorkflows: OpenWorkflowInput[] = [
        {
          workflow_id: workflow.id,
          vacancy_id: vacancy.id,
          vacancy_title: vacancy.title,
          vacancy_requirements: vacancy.requirements,
          current_match_score: workflow.match_score || "partial",
          gap_questions: (workflow.gap_questions || []).map((q) => ({
            id: q.id,
            requirement: q.requirement,
            classification: q.classification,
            status: q.status,
          })),
        },
      ];

      // 4. Call AI enrichment
      const result = await processEnrichmentAnswers(
        apiKey,
        model,
        JSON.stringify(profile),
        answers,
        openWorkflows,
        settings?.prompts?.enrichment,
      );

      // 5. Apply profile updates
      const now = new Date().toISOString();
      const updatedProfile = applyProfileUpdates(profile, result.profile_updates, now);
      await writeProfileJson(repoPath, activeCandidateFolder, updatedProfile);

      // 6. Apply question resolutions + updated match score to workflow
      const resolutionMap = new Map(result.question_resolutions.map((r) => [r.question_id, r]));
      const updatedQuestions = (workflow.gap_questions || []).map((q) => {
        const res = resolutionMap.get(q.id);
        if (!res) return q;
        return { ...q, resolved_as: res.resolved_as as "confirmed" | "gap" | "unknown" };
      });

      const wfUpdate = result.workflow_updates.find((u) => u.workflow_id === workflow.id);
      const allAnswered = updatedQuestions.every((q) => q.status === "answered");
      const updatedWf: Workflow = {
        ...workflow,
        gap_questions: updatedQuestions,
        match_score: wfUpdate?.new_match_score ?? workflow.match_score,
        status: allAnswered ? "pack_in_progress" : workflow.status,
        updated_at: now,
      };

      await writeWorkflow(repoPath, activeCandidateFolder, vacancy.id, updatedWf);
      setWorkflow(updatedWf);
      setShowRecalcNotice(false);

      // Build base success summary
      const confirmed = result.profile_updates.skills_to_confirm.length;
      const gaps = result.profile_updates.skills_to_gap.length;
      const inferred = result.profile_updates.new_inferences.length;
      const scoreChanged = wfUpdate?.score_changed ?? false;

      setEnrichmentSuccess(
        language === "nl"
          ? `Profiel bijgewerkt: ${confirmed} vaardigheid${confirmed !== 1 ? "en" : ""} bevestigd${gaps > 0 ? `, ${gaps} lacune${gaps !== 1 ? "s" : ""} vastgelegd` : ""}${inferred > 0 ? `, ${inferred} nieuwe inferentie${inferred !== 1 ? "s" : ""}` : ""}${scoreChanged ? " · Matchscore bijgewerkt" : ""}. Nieuwe vragen zoeken…`
          : `Profile updated: ${confirmed} skill${confirmed !== 1 ? "s" : ""} confirmed${gaps > 0 ? `, ${gaps} gap${gaps !== 1 ? "s" : ""} recorded` : ""}${inferred > 0 ? `, ${inferred} new inference${inferred !== 1 ? "s" : ""}` : ""}${scoreChanged ? " · Match score updated" : ""}. Surfacing new questions…`,
      );

      // Re-run analysis against the enriched profile to surface any new gap questions.
      // Pass the current question list so answered questions are preserved and only
      // genuinely new requirements get appended.
      const newQuestionCount = await runAIVacancyAnalysis(updatedWf.gap_questions);

      setEnrichmentSuccess(
        language === "nl"
          ? `Profiel bijgewerkt: ${confirmed} vaardigheid${confirmed !== 1 ? "en" : ""} bevestigd${gaps > 0 ? `, ${gaps} lacune${gaps !== 1 ? "s" : ""} vastgelegd` : ""}${inferred > 0 ? `, ${inferred} nieuwe inferentie${inferred !== 1 ? "s" : ""}` : ""}${scoreChanged ? " · Matchscore bijgewerkt" : ""}${newQuestionCount > 0 ? ` · ${newQuestionCount} nieuwe vraag${newQuestionCount !== 1 ? "en" : ""} gevonden` : " · Geen nieuwe vragen"}.`
          : `Profile updated: ${confirmed} skill${confirmed !== 1 ? "s" : ""} confirmed${gaps > 0 ? `, ${gaps} gap${gaps !== 1 ? "s" : ""} recorded` : ""}${inferred > 0 ? `, ${inferred} new inference${inferred !== 1 ? "s" : ""}` : ""}${scoreChanged ? " · Match score updated" : ""}${newQuestionCount > 0 ? ` · ${newQuestionCount} new question${newQuestionCount !== 1 ? "s" : ""} surfaced` : " · No new questions"}.`,
      );
    } catch (err: any) {
      console.error("AI enrichment failed", err);
      setEnrichmentError(err?.message || "Enrichment processing failed.");
    } finally {
      setProcessingEnrichment(false);
    }
  }

  const handleDraftEmail = async () => {
    if (!workflow || !vacancy) return;
    const pendingQuestions = (workflow.gap_questions || []).filter(q => q.status === "unanswered");
    if (pendingQuestions.length === 0) return;

    const emailSubject = language === "nl"
      ? `Aanvullende vragen: ${vacancy.title} - ${vacancy.client}`
      : `Additional questions: ${vacancy.title} - ${vacancy.client}`;

    const questionsList = pendingQuestions.map((q, i) => `${i + 1}. ${language === "nl" ? q.question_nl : q.question_en}`).join("\n\n");

    const emailBody = language === "nl"
      ? `Beste ${candidateName},\n\nOm je profiel zo goed mogelijk af te stemmen op de vacature ${vacancy.title} bij ${vacancy.client}, heb ik de volgende aanvullende vragen:\n\n${questionsList}\n\nZou je deze kunnen beantwoorden en naar mij terugsturen?\n\nMet vriendelijke groet,\n${user?.displayName || "Lens Recruiter"}`
      : `Dear ${candidateName},\n\nTo align your profile as closely as possible with the vacancy ${vacancy.title} at ${vacancy.client}, I have the following additional questions:\n\n${questionsList}\n\nCould you please answer these and send them back to me?\n\nBest regards,\n${user?.displayName || "Lens Recruiter"}`;

    try {
      await navigator.clipboard.writeText(emailBody);
      setEmailDraftStatus("copied");
      setTimeout(() => setEmailDraftStatus("idle"), 3000);
      
      const mailtoUrl = `mailto:${candidateEmail}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.open(mailtoUrl, "_self");
    } catch (err) {
      console.error("Failed to copy/draft email", err);
    }
  };

  const handleParseEmailResponse = async () => {
    if (!repoPath || !activeCandidateFolder || !workflow || !vacancy || !emailTextToParse.trim()) return;

    setParsingEmail(true);
    setParseEmailError(null);
    setParseEmailSuccess(null);

    try {
      const apiKey = settings?.anthropic_api_key || "";
      const model = settings?.anthropic_model || "gemini-2.5-flash";

      if (!apiKey) {
        throw new Error(
          language === "nl"
            ? "AI API-sleutel ontbreekt in Instellingen."
            : "AI API Key is missing in Settings."
        );
      }

      const unansweredQuestions = (workflow.gap_questions || []).filter(q => q.status === "unanswered");
      if (unansweredQuestions.length === 0) {
        throw new Error(
          language === "nl"
            ? "Geen onbeantwoorde vragen gevonden om op te lossen."
            : "No unanswered questions found to resolve."
        );
      }

      const parsedAnswers = await parseEmailResponseWithAI(
        apiKey,
        model,
        emailTextToParse,
        unansweredQuestions
      );

      if (parsedAnswers.length === 0) {
        throw new Error(
          language === "nl"
            ? "De AI kon geen antwoorden op de vragen in de tekst vinden. Controleer of de tekst de antwoorden bevat."
            : "AI could not find answers to any questions in the text. Make sure the text contains the answers."
        );
      }

      // Update local workflow.gap_questions with the answers extracted
      const updatedQuestions = (workflow.gap_questions || []).map((q) => {
        const parsed = parsedAnswers.find(pa => pa.question_id === q.id);
        if (parsed && parsed.resolved_as !== "unknown") {
          return {
            ...q,
            status: "answered" as const,
            answer: parsed.answer,
            answered_at: new Date().toISOString(),
            resolved_as: parsed.resolved_as,
          };
        }
        return q;
      });

      const updatedWf: Workflow = {
        ...workflow,
        gap_questions: updatedQuestions,
        updated_at: new Date().toISOString(),
      };

      await writeWorkflow(repoPath, activeCandidateFolder, vacancy.id, updatedWf);
      setWorkflow(updatedWf);
      setEmailTextToParse("");
      setParseEmailSuccess(
        language === "nl"
          ? `Succesvol ${parsedAnswers.length} antwoord(en) verwerkt! Controleer de antwoorden hieronder en klik op 'Verwerken met AI' om het profiel bij te werken.`
          : `Successfully processed ${parsedAnswers.length} answer(s)! Review the answers below and click 'Process with AI' to update the candidate profile.`
      );
    } catch (err: any) {
      console.error("Failed to parse email", err);
      setParseEmailError(err?.message || "Failed to process response.");
    } finally {
      setParsingEmail(false);
    }
  };

  async function handleGenerateDocument(type: "tailored_cv" | "motivatiebrief") {
    setDocGenError(null);
    if (!repoPath || !activeCandidateFolder || !workflow || !vacancy) return;

    const apiKey = settings?.anthropic_api_key || "";
    const model = settings?.anthropic_model || "gemini-2.5-flash";
    if (!apiKey) {
      setDocGenError(
        language === "nl"
          ? "AI API-sleutel ontbreekt in Instellingen. Voeg een API-sleutel toe om documenten te genereren."
          : "AI API Key is missing in Settings. Please add an API Key to enable document generation."
      );
      return;
    }

    setGeneratingDoc(type);
    try {
      const cvText = await readBestCvText(repoPath, activeCandidateFolder);

      const profile = await readProfileJson(repoPath, activeCandidateFolder);

      const content = await generateDocument(
        apiKey,
        model,
        {
          document_type: type,
          language: "nl",
          cv_text: cvText,
          profile_json: JSON.stringify(profile ?? {}),
          vacancy_json: JSON.stringify(vacancy),
        },
        settings?.prompts?.document_generation,
      );

      // v2.0: no filesystem write. Render the docx client-side and offer
      // it as a browser download; store the content JSON inline in the
      // workflow.documents object so subsequent loads can render/edit it.
      let docxBytes: Uint8Array;
      const fileBase = type === "tailored_cv" ? "tailored_cv" : "motivatiebrief";
      if (type === "tailored_cv") {
        docxBytes = await formatCV(content as TailoredCVContent, candidateName);
      } else {
        docxBytes = await formatLetter(
          content as MotivatiebriefContent,
          candidateName,
          vacancy.title,
          vacancy.client,
        );
      }
      downloadBuffer(
        docxBytes,
        `${fileBase}_${candidateName.replace(/\s+/g, "_")}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      );

      const docObj: any = {
        path: "",           // v2.0: no persisted blob yet
        content_path: "",   // v2.0: content is inline (see below)
        content,            // inline JSON — enables re-render / download later
        generated_at: new Date().toISOString(),
        status: "draft" as const,
        submitted_at: null,
      };

      const updatedDocs = { ...(workflow.documents || {}), [type]: docObj };
      const cvGen = !!updatedDocs.tailored_cv;
      const letterGen = !!updatedDocs.motivatiebrief;
      const nextStatus = cvGen && letterGen ? "ready_to_submit" : "pack_in_progress";

      const updatedWf: Workflow = {
        ...workflow,
        documents: updatedDocs,
        status: nextStatus as any,
        updated_at: new Date().toISOString(),
      };

      await writeWorkflow(repoPath, activeCandidateFolder, vacancy.id, updatedWf);
      setWorkflow(updatedWf);
      if (type === "tailored_cv") {
        setLoadedCvContent(content as TailoredCVContent);
      } else {
        setLoadedLetterContent(content as MotivatiebriefContent);
      }
    } catch (err: any) {
      console.error("Failed to generate document", err);
      setDocGenError(err?.message || String(err));
    } finally {
      setGeneratingDoc(null);
    }
  }

  async function handleDownload(type: "tailored_cv" | "motivatiebrief") {
    if (!workflow) return;
    const doc: any = type === "tailored_cv" ? workflow.documents?.tailored_cv : workflow.documents?.motivatiebrief;
    const content = doc?.content ?? (type === "tailored_cv" ? loadedCvContent : loadedLetterContent);
    if (!content) return;

    try {
      const candidateName = activeCandidateFolder ?? "consultant";
      const fileBase = type === "tailored_cv" ? "tailored_cv" : "motivatiebrief";
      const bytes =
        type === "tailored_cv"
          ? await formatCV(content as TailoredCVContent, candidateName)
          : await formatLetter(
              content as MotivatiebriefContent,
              candidateName,
              vacancy?.title ?? "",
              vacancy?.client ?? "",
            );
      downloadBuffer(
        bytes,
        `${fileBase}_${String(candidateName).replace(/\s+/g, "_")}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      );
    } catch (err) {
      console.error("Failed to re-render document for download", err);
    }
  }

  async function handleMarkSubmitted(type: "tailored_cv" | "motivatiebrief") {
    if (!repoPath || !activeCandidateFolder || !workflow || !vacancy) return;
    const doc = type === "tailored_cv" ? workflow.documents?.tailored_cv : workflow.documents?.motivatiebrief;
    if (!doc) return;

    const updatedDoc = { ...doc, status: "submitted" as const, submitted_at: new Date().toISOString() };
    const updatedDocs = { ...(workflow.documents || {}), [type]: updatedDoc };

    const allSubmitted = ["tailored_cv", "motivatiebrief"].every(
      (k) => (updatedDocs as any)[k]?.status === "submitted",
    );

    const updatedWf: Workflow = {
      ...workflow,
      documents: updatedDocs,
      status: allSubmitted ? ("submitted" as any) : workflow.status,
      updated_at: new Date().toISOString(),
    };

    await writeWorkflow(repoPath, activeCandidateFolder, vacancy.id, updatedWf);
    setWorkflow(updatedWf);
  }

  // Render score gauge color helpers
  function getScoreColorClass(score?: string | null) {
    switch (score) {
      case "strong":
        return "text-emerald-600 bg-emerald-50 border-emerald-200";
      case "good":
        return "text-cyan-600 bg-cyan-50 border-cyan-200";
      case "partial":
        return "text-amber-600 bg-amber-50 border-amber-200";
      case "weak":
        return "text-rose-600 bg-rose-50 border-rose-200";
      default:
        return "text-slate-500 bg-slate-50 border-slate-200";
    }
  }

  // getScoreMeterColor is unused since we set color inline/dynamically via ref

  function getScorePercentage(score?: string | null) {
    switch (score) {
      case "strong":
        return 90;
      case "good":
        return 70;
      case "partial":
        return 45;
      case "weak":
        return 25;
      default:
        return 10;
    }
  }

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-slate-800" />
          <p className="text-sm font-medium text-slate-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (error || !workflow || !vacancy) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <div className="rounded-xl border border-red-200 bg-red-50 p-8">
          <h2 className="text-lg font-bold text-red-800 mb-2">
            {language === "nl" ? "Fout bij laden van workspace" : "Workspace Load Error"}
          </h2>
          <p className="text-sm text-red-600 mb-6">{error || "Workflow not found"}</p>
          <button
            onClick={goBack}
            className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 cursor-pointer"
          >
            {getBackLabel()}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      
      {/* Workspace Header */}
      <header className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-200/60 pb-6 mb-8 gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {vacancy.client}
            </span>
            <span className="text-slate-300">•</span>
            <span className="text-xs font-medium text-slate-500">{candidateName}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {vacancy.title}
            </h1>
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider ${
              workflow.status === "submitted" || workflow.status === "placed"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                : workflow.status === "rejected"
                ? "bg-red-50 text-red-700 border border-red-100"
                : "bg-slate-100 text-slate-700 border border-slate-200/50"
            }`}>
              {t(`workflow.status.${workflow.status}` as any)}
            </span>
          </div>
        </div>

        <button
          onClick={goBack}
          className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-650 hover:bg-slate-50 transition-colors shadow-2xs hover:shadow-xs cursor-pointer focus:outline-none"
        >
          ← {getBackLabel()}
        </button>
      </header>

      {/* AI Analysis / Extraction State Overlay or Alert Banner */}
      {analyzing && (
        <div className="mb-8 rounded-xl border border-indigo-100 bg-indigo-50/50 p-6 shadow-sm text-center">
          <div className="flex flex-col items-center gap-3">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
            <h3 className="text-sm font-bold text-slate-800">
              {language === "nl" ? "AI-matchinganalyse uitvoeren…" : "Running AI Match Analysis…"}
            </h3>
            <p className="text-xs text-slate-500 max-w-md leading-relaxed">
              {language === "nl" 
                ? "We vergelijken het geüploade master-CV met de vacature-eisen en formuleren gerichte gap-vragen."
                : "We are comparing the uploaded master CV with vacancy requirements and formulating targeted gap questions."}
            </p>
          </div>
        </div>
      )}

      {analysisError && (
        <div className="mb-8 rounded-xl border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="text-base text-rose-500">⚠️</span>
            <div className="flex-1">
              <h4 className="text-xs font-bold text-rose-800 uppercase tracking-wider">
                {language === "nl" ? "AI Analyse Mislukt" : "AI Analysis Failed"}
              </h4>
              <p className="text-xs text-rose-600 mt-1">{analysisError}</p>
              <button
                onClick={() => void runAIVacancyAnalysis()}
                className="mt-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
              >
                {language === "nl" ? "Opnieuw proberen" : "Retry Analysis"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Tab Control (Premium folder-style) */}
      <div className="flex border-b border-slate-200 mb-8 gap-1.5">
        {([
          { id: "overview", labelKey: "workspace.tab.overview" },
          { id: "enrichment", labelKey: "workspace.tab.enrichment" },
          { id: "documents", labelKey: "workspace.tab.documents" },
        ] as const).map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 -mb-px border text-xs font-bold uppercase tracking-wider rounded-t-xl transition-all duration-150 cursor-pointer focus:outline-none ${
                isActive
                  ? "bg-white border-slate-200 border-b-white text-slate-900 shadow-2xs font-extrabold"
                  : "bg-slate-50/50 border-slate-100 border-b-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-650"
              }`}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Tab Panels */}
      {!analyzing && (
        <div className="space-y-6">

          {/* Recalculate Notice Banner */}
          {showRecalcNotice && workflow.match_score && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 flex items-center justify-between gap-4 animate-in fade-in duration-200">
              <div className="flex items-center gap-3">
                <span className="text-base text-indigo-500">💡</span>
                <p className="text-xs text-indigo-800 font-medium">
                  {language === "nl"
                    ? "Je hebt zojuist nieuwe informatie beantwoord. Start de AI-matchinganalyse opnieuw om de score bij te werken."
                    : "You have answered new profile information. Re-run matching analysis to update the match score."}
                </p>
              </div>
              <button
                onClick={() => void runAIVacancyAnalysis()}
                className="shrink-0 rounded-lg bg-indigo-650 hover:bg-indigo-750 text-white px-3.5 py-1.5 text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
              >
                {language === "nl" ? "Match Score Updaten" : "Recalculate Match"}
              </button>
            </div>
          )}

          {/* OVERVIEW TAB */}
          {activeTab === "overview" && workflow.match_score && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in duration-100">
              
              {/* Score Meter Panel */}
              <div className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs flex flex-col items-center justify-center text-center">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-6">
                  {t("workspace.match_score")}
                </h3>

                {/* Meter Gauge */}
                <div className="relative flex items-center justify-center mb-6">
                  {/* Outer circle */}
                  <svg className="w-36 h-36 transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      className="stroke-slate-100 fill-none"
                      strokeWidth="10"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      className={`fill-none transition-all duration-500 ease-out`}
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - getScorePercentage(workflow.match_score) / 100)}`}
                      style={{ color: `var(--color-meter, rgb(99, 102, 241))` }}
                    />
                  </svg>
                  {/* Override circle color inline to match custom dashboard variables or Tailwind HSLs */}
                  <div
                    className="hidden"
                    ref={(el) => {
                      if (el) {
                        const score = workflow.match_score;
                        let color = "rgb(148, 163, 184)"; // slate-400
                        if (score === "strong") color = "rgb(16, 185, 129)"; // emerald-500
                        if (score === "good") color = "rgb(6, 182, 212)"; // cyan-500
                        if (score === "partial") color = "rgb(245, 158, 11)"; // amber-500
                        if (score === "weak") color = "rgb(244, 63, 94)"; // rose-500
                        el.parentElement?.querySelector("svg circle:last-child")?.setAttribute("style", `stroke: ${color}`);
                      }
                    }}
                  />
                  <div className="absolute flex flex-col items-center">
                    <span className="text-3xl font-extrabold text-slate-800">
                      {getScorePercentage(workflow.match_score)}%
                    </span>
                  </div>
                </div>

                <span className={`inline-flex rounded-full px-3.5 py-1 text-xs font-bold uppercase tracking-wider border ${getScoreColorClass(workflow.match_score)}`}>
                  {t(`match.score.${workflow.match_score}` as any)}
                </span>

                <button
                  onClick={() => void runAIVacancyAnalysis()}
                  className="mt-6 text-xs text-indigo-600 hover:text-indigo-800 font-semibold underline underline-offset-4 cursor-pointer"
                >
                  {language === "nl" ? "Analyse opnieuw uitvoeren" : "Re-run Match Analysis"}
                </button>
              </div>

              {/* Rationale and Confirmed Requirements (2 cols) */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Rationale card */}
                <div className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">
                  <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-3 mb-4">
                    {t("workspace.match_rationale")}
                  </h3>
                  <div className="text-sm text-slate-650 leading-relaxed whitespace-pre-wrap">
                    {workflow.match_rationale || (
                      <span className="text-slate-400 italic">
                        {language === "nl" ? "Geen rationale beschikbaar." : "No rationale available."}
                      </span>
                    )}
                  </div>
                </div>

                {/* Confirmed requirements card */}
                <div className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">
                  <h3 className="text-sm font-bold text-slate-800 border-b border-slate-100 pb-3 mb-4">
                    {language === "nl" ? "Bevestigde Vaardigheden & Eisen" : "Confirmed Skills & Requirements"}
                  </h3>
                  {workflow.confirmed_requirements && workflow.confirmed_requirements.length > 0 ? (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {workflow.confirmed_requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs font-medium text-slate-650 bg-slate-50/70 border border-slate-100 rounded-lg p-2.5">
                          <span className="text-emerald-500 font-bold shrink-0">✓</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-400 italic">
                      {language === "nl" ? "Geen expliciet bevestigde eisen gevonden." : "No explicitly confirmed requirements found."}
                    </p>
                  )}
                </div>

              </div>

            </div>
          )}

          {/* ENRICHMENT TAB (GAP QUESTIONS) */}
          {activeTab === "enrichment" && (
            <div className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs space-y-6 animate-in fade-in duration-100">
              
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 tracking-tight">
                    {t("workspace.gap_questions")}
                  </h2>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    {language === "nl"
                      ? "Beantwoord de vragen hieronder en klik op 'Verwerken met AI' om het profiel bij te werken en de matchscore opnieuw te berekenen."
                      : "Answer the questions below, then click 'Process with AI' to update the profile and re-evaluate the match score."}
                  </p>
                </div>

                <div className="shrink-0 flex items-center gap-2 flex-wrap">
                  {/* Draft Email button */}
                  {(workflow.gap_questions || []).some(q => q.status === "unanswered") && (
                    <button
                      type="button"
                      onClick={() => void handleDraftEmail()}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-xs font-semibold shadow-2xs transition-all cursor-pointer focus:outline-none"
                    >
                      ✉ {emailDraftStatus === "copied" 
                        ? (language === "nl" ? "Gekopieerd!" : "Copied!") 
                        : (language === "nl" ? "E-mail Concept" : "Draft Email")}
                    </button>
                  )}

                  {/* AI Process button — enabled once at least one question is answered */}
                  {(() => {
                    const answeredCount = (workflow.gap_questions || []).filter(
                      (q) => q.status === "answered" && q.answer,
                    ).length;
                    return (
                      <button
                        type="button"
                        onClick={() => void handleAIEnrichment()}
                        disabled={processingEnrichment || answeredCount === 0}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-650 hover:bg-indigo-750 text-white px-4 py-2 text-xs font-semibold shadow-xs transition-all cursor-pointer focus:outline-none disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
                      >
                        {processingEnrichment ? (
                          <>
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-300 border-t-white" />
                            {language === "nl" ? "Verwerken…" : "Processing…"}
                          </>
                        ) : (
                          <>
                            ✦ {language === "nl"
                              ? `Verwerken met AI${answeredCount > 0 ? ` (${answeredCount})` : ""}`
                              : `Process with AI${answeredCount > 0 ? ` (${answeredCount})` : ""}`}
                          </>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* Paste response section */}
              {(workflow.gap_questions || []).some(q => q.status === "unanswered") && (
                <div className="bg-slate-50/60 border border-slate-200/50 rounded-xl p-4.5 space-y-3 animate-in fade-in duration-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      {language === "nl" ? "E-mailreactie van kandidaat verwerken" : "Process candidate email response"}
                    </h3>
                    <span className="text-[10px] text-slate-400">
                      {language === "nl" ? "AI vult automatisch de antwoorden in" : "AI will auto-fill the answers below"}
                    </span>
                  </div>
                  <textarea
                    rows={4}
                    value={emailTextToParse}
                    onChange={(e) => setEmailTextToParse(e.target.value)}
                    placeholder={language === "nl" 
                      ? "Plak hier de volledige e-mailreactie van de consultant..." 
                      : "Paste the consultant's complete email response here..."}
                    className="w-full rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-800 placeholder-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                  />
                  {parseEmailError && (
                    <p className="text-[11px] font-semibold text-rose-600">{parseEmailError}</p>
                  )}
                  {parseEmailSuccess && (
                    <p className="text-[11px] font-semibold text-emerald-600">{parseEmailSuccess}</p>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => void handleParseEmailResponse()}
                      disabled={parsingEmail || !emailTextToParse.trim()}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 text-white disabled:text-slate-400 px-4 py-2 text-xs font-semibold shadow-xs transition-all cursor-pointer focus:outline-none disabled:cursor-not-allowed"
                    >
                      {parsingEmail ? (
                        <>
                          <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-white" />
                          {language === "nl" ? "Analyseren…" : "Analyzing…"}
                        </>
                      ) : (
                        <>
                          ✦ {language === "nl" ? "Reactie analyseren met AI" : "Analyze response with AI"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Enrichment feedback banners */}
              {enrichmentSuccess && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 flex items-start gap-2.5 animate-in fade-in duration-150">
                  <span className="text-emerald-500 text-sm shrink-0">✓</span>
                  <p className="text-xs font-semibold text-emerald-800">{enrichmentSuccess}</p>
                  <button
                    type="button"
                    onClick={() => setEnrichmentSuccess(null)}
                    className="ml-auto text-emerald-400 hover:text-emerald-600 text-xs cursor-pointer shrink-0"
                  >
                    ✕
                  </button>
                </div>
              )}
              {enrichmentError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5 flex items-start gap-2.5 animate-in fade-in duration-150">
                  <span className="text-rose-500 text-sm shrink-0">⚠</span>
                  <p className="text-xs font-semibold text-rose-800">{enrichmentError}</p>
                  <button
                    type="button"
                    onClick={() => setEnrichmentError(null)}
                    className="ml-auto text-rose-400 hover:text-rose-600 text-xs cursor-pointer shrink-0"
                  >
                    ✕
                  </button>
                </div>
              )}

              {(!workflow.gap_questions || workflow.gap_questions.length === 0) ? (
                <div className="py-12 text-center">
                  <p className="text-sm text-slate-400 italic">
                    {language === "nl" ? "Geen gap-vragen uitstaand." : "No pending gap questions."}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {workflow.gap_questions.map((q) => {
                    const isAnswered = q.status === "answered";
                    const isEditing = editingQuestionId === q.id;
                    const questionText = language === "nl" ? q.question_nl : q.question_en;

                    return (
                      <article key={q.id} className="py-6 first:pt-0 last:pb-0">
                        
                        {/* Question Header */}
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="space-y-1.5 flex-1">
                            {/* Tags */}
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider border ${
                                q.classification === "gap"
                                  ? "bg-rose-50 text-rose-700 border-rose-100"
                                  : "bg-amber-50 text-amber-700 border-amber-100"
                              }`}>
                                {t(`gap.classification.${q.classification}` as any)}
                              </span>
                              
                              {isAnswered && (
                                <span className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider border ${
                                  q.resolved_as === "confirmed"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                    : "bg-slate-100 text-slate-650 border-slate-200"
                                }`}>
                                  {q.resolved_as === "confirmed"
                                    ? (language === "nl" ? "Bevestigd als vaardigheid" : "Confirmed Skill")
                                    : (language === "nl" ? "Geklasseerd als lacune" : "Confirmed Gap")}
                                </span>
                              )}
                            </div>

                            {/* Requirement detail */}
                            <p className="text-2xs uppercase tracking-wider font-semibold text-slate-400">
                              {language === "nl" ? "Vereiste" : "Requirement"}: <span className="text-slate-500 font-medium">{q.requirement}</span>
                            </p>

                            {/* Question Title */}
                            <h4 className="text-sm font-bold text-slate-800 leading-snug">
                              {questionText}
                            </h4>

                            {q.classification === "inferred" && q.inference_basis && (
                              <p className="text-3xs text-slate-400 leading-relaxed max-w-2xl bg-slate-50 rounded px-2 py-1 italic">
                                💡 {t("workspace.question.basis")}: {q.inference_basis}
                              </p>
                            )}
                          </div>

                          {/* Edit/Answer Button */}
                          {!isEditing && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingQuestionId(q.id);
                                setAnswerText(q.answer || "");
                                setResolution(q.resolved_as === "gap" ? "gap" : "confirmed");
                                setYearsOfExp(1);
                              }}
                              className="self-start text-xs font-semibold text-indigo-650 hover:text-indigo-800 underline underline-offset-2 cursor-pointer focus:outline-none"
                            >
                              {isAnswered 
                                ? (language === "nl" ? "Wijzigen" : "Edit Answer")
                                : (language === "nl" ? "Beantwoorden" : "Answer Question")}
                            </button>
                          )}
                        </div>

                        {/* Answer Display */}
                        {isAnswered && !isEditing && (
                          <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-450">
                              {t("workspace.question.your_answer")}
                            </p>
                            <p className="text-xs text-slate-700 font-medium mt-1 leading-relaxed">
                              {q.answer}
                            </p>
                            {q.answered_at && (
                              <span className="text-[9px] text-slate-400 font-medium mt-2 block">
                                {language === "nl" ? "Ingevuld op" : "Answered at"}: {new Date(q.answered_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Interactive Form Panel */}
                        {isEditing && (
                          <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/15 p-5 space-y-4 animate-in slide-in-from-top-2 duration-150">
                            
                            {/* Answer Textbox */}
                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-700 block">
                                {t("workspace.question.your_answer")}
                              </label>
                              <textarea
                                value={answerText}
                                onChange={(e) => setAnswerText(e.target.value)}
                                placeholder={t("workspace.question.placeholder")}
                                rows={3}
                                className="w-full text-xs rounded-lg border border-slate-200 bg-white p-3 text-slate-700 shadow-2xs focus:border-indigo-500 focus:outline-none transition-colors"
                              />
                            </div>

                            {/* Resolution Controls */}
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between border-t border-slate-100/60 pt-4">
                              <div className="space-y-2">
                                <span className="text-xs font-bold text-slate-700 block">
                                  {language === "nl" ? "Besluit / Classificatie" : "Resolution / Classification"}
                                </span>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setResolution("confirmed")}
                                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                                      resolution === "confirmed"
                                        ? "bg-white border-emerald-500 text-emerald-700 shadow-2xs"
                                        : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                                    }`}
                                  >
                                    {t("workspace.question.mark_skill")}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setResolution("gap")}
                                    className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                                      resolution === "gap"
                                        ? "bg-white border-slate-400 text-slate-700 shadow-2xs"
                                        : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                                    }`}
                                  >
                                    {t("workspace.question.mark_gap")}
                                  </button>
                                </div>
                              </div>

                              {/* Years of experience if skill confirmed */}
                              {resolution === "confirmed" && (
                                <div className="space-y-1.5">
                                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
                                    {language === "nl" ? "Jaren Ervaring" : "Years of Experience"}
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={40}
                                    value={yearsOfExp}
                                    onChange={(e) => setYearsOfExp(Number(e.target.value))}
                                    className="w-20 text-xs rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 font-semibold focus:outline-none"
                                  />
                                </div>
                              )}
                            </div>

                            {/* Actions buttons */}
                            <div className="flex justify-end gap-2 border-t border-slate-100/60 pt-3">
                              <button
                                type="button"
                                onClick={() => setEditingQuestionId(null)}
                                className="px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-650 hover:bg-slate-50 transition-colors cursor-pointer"
                              >
                                {t("common.cancel")}
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => void handleSaveAnswer(q.id)}
                                disabled={savingAnswerId === q.id || !answerText.trim()}
                                className="px-4.5 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-xs transition-colors cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed"
                              >
                                {savingAnswerId === q.id ? "…" : t("workspace.question.save_answer")}
                              </button>
                            </div>

                          </div>
                        )}

                      </article>
                    );
                  })}
                </div>
              )}

            </div>
          )}

          {/* DOCUMENTS GENERATION TAB */}
          {activeTab === "documents" && (
            <div className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs space-y-6 animate-in fade-in duration-100">
              
              <div className="border-b border-slate-100 pb-4">
                <h2 className="text-lg font-bold text-slate-800 tracking-tight">
                  {t("workspace.documents.title")}
                </h2>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  {language === "nl"
                    ? "Genereer en beheer de indieningsdocumenten voor dit inkoopkanaal. De documenten worden opgeslagen in de workflow-map."
                    : "Generate and manage submission artifacts for this portal route. Documents are stored locally in the workflow directory."}
                </p>
              </div>

              {docGenError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5 flex items-start gap-2.5 animate-in fade-in duration-150">
                  <span className="text-rose-500 text-sm shrink-0">⚠</span>
                  <p className="text-xs font-semibold text-rose-800">{docGenError}</p>
                  <button
                    type="button"
                    onClick={() => setDocGenError(null)}
                    className="ml-auto text-rose-400 hover:text-rose-600 text-xs cursor-pointer shrink-0"
                  >
                    ✕
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Tailored CV Doc Card */}
                {(() => {
                  const doc = workflow.documents?.tailored_cv;
                  const isGenerating = generatingDoc === "tailored_cv";
                  return (
                    <article className="border border-slate-200/60 rounded-xl p-5 flex flex-col justify-between hover:shadow-xs transition-shadow bg-white/70">
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h4 className="text-sm font-extrabold text-slate-800">
                            {t("workspace.documents.cv_label")}
                          </h4>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                            doc?.status === "draft"
                              ? "bg-amber-50 text-amber-700 border border-amber-100"
                              : "bg-slate-55 bg-slate-50 text-slate-500 border border-slate-200/40"
                          }`}>
                            {doc?.status === "draft"
                              ? t("document.status.draft")
                              : (language === "nl" ? "Niet gegenereerd" : "Not Generated")}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed max-w-sm">
                          {language === "nl"
                            ? "Een op maat gemaakte versie van het CV waarin de specifieke eisen en competenties van deze rol worden benadrukt."
                            : "A customized CV highlighting matching experiences and competencies for this role."}
                        </p>

                        {doc?.generated_at && (
                          <p className="text-[10px] font-medium text-slate-400 mt-4">
                            {t("workspace.documents.generated_at")}: {new Date(doc.generated_at).toLocaleString()}
                          </p>
                        )}
                        {doc?.submitted_at && (
                          <p className="text-[10px] font-medium text-emerald-500 mt-1">
                            {language === "nl" ? "Ingediend op" : "Submitted"}: {new Date(doc.submitted_at).toLocaleString()}
                          </p>
                        )}
                      </div>

                      <div className="mt-6 border-t border-slate-50 pt-4 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex gap-2">
                          {doc && (
                            <button
                              type="button"
                              onClick={() => void handleDownload("tailored_cv")}
                              className="rounded-lg border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 text-xs font-semibold shadow-2xs transition-all cursor-pointer"
                            >
                              {language === "nl" ? "Downloaden" : "Download"}
                            </button>
                          )}
                          {doc && doc.status !== "submitted" && (
                            <button
                              type="button"
                              onClick={() => void handleMarkSubmitted("tailored_cv")}
                              className="rounded-lg border border-emerald-200 hover:border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-2 text-xs font-semibold transition-all cursor-pointer"
                            >
                              {language === "nl" ? "Markeer als ingediend" : "Mark as Submitted"}
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => void handleGenerateDocument("tailored_cv")}
                          disabled={isGenerating || !!generatingDoc}
                          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-xs font-semibold shadow-2xs hover:shadow-xs transition-all cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed"
                        >
                          {isGenerating ? (
                            <span className="flex items-center gap-1">
                              <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-white" />
                              …
                            </span>
                          ) : doc ? (
                            t("document.regenerate")
                          ) : (
                            t("workspace.documents.generate")
                          )}
                        </button>
                      </div>
                    </article>
                  );
                })()}

                {/* Motivatiebrief Doc Card */}
                {(() => {
                  const doc = workflow.documents?.motivatiebrief;
                  const isGenerating = generatingDoc === "motivatiebrief";
                  return (
                    <article className="border border-slate-200/60 rounded-xl p-5 flex flex-col justify-between hover:shadow-xs transition-shadow bg-white/70">
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h4 className="text-sm font-extrabold text-slate-800">
                            {t("workspace.documents.letter_label")}
                          </h4>
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                            doc?.status === "draft"
                              ? "bg-amber-50 text-amber-700 border border-amber-100"
                              : "bg-slate-55 bg-slate-50 text-slate-500 border border-slate-200/40"
                          }`}>
                            {doc?.status === "draft"
                              ? t("document.status.draft")
                              : (language === "nl" ? "Niet gegenereerd" : "Not Generated")}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed max-w-sm">
                          {language === "nl"
                            ? "Een begeleidend schrijven geoptimaliseerd voor deze vacature op basis van uw CV en antwoorden."
                            : "A personalized cover letter framed around vacancy requirements and profile answers."}
                        </p>

                        {doc?.generated_at && (
                          <p className="text-[10px] font-medium text-slate-400 mt-4">
                            {t("workspace.documents.generated_at")}: {new Date(doc.generated_at).toLocaleString()}
                          </p>
                        )}
                        {doc?.submitted_at && (
                          <p className="text-[10px] font-medium text-emerald-500 mt-1">
                            {language === "nl" ? "Ingediend op" : "Submitted"}: {new Date(doc.submitted_at).toLocaleString()}
                          </p>
                        )}
                      </div>

                      <div className="mt-6 border-t border-slate-50 pt-4 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex gap-2">
                          {doc && (
                            <button
                              type="button"
                              onClick={() => void handleDownload("motivatiebrief")}
                              className="rounded-lg border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 text-xs font-semibold shadow-2xs transition-all cursor-pointer"
                            >
                              {language === "nl" ? "Downloaden" : "Download"}
                            </button>
                          )}
                          {doc && doc.status !== "submitted" && (
                            <button
                              type="button"
                              onClick={() => void handleMarkSubmitted("motivatiebrief")}
                              className="rounded-lg border border-emerald-200 hover:border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-2 text-xs font-semibold transition-all cursor-pointer"
                            >
                              {language === "nl" ? "Markeer als ingediend" : "Mark as Submitted"}
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => void handleGenerateDocument("motivatiebrief")}
                          disabled={isGenerating || !!generatingDoc}
                          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-xs font-semibold shadow-2xs hover:shadow-xs transition-all cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed"
                        >
                          {isGenerating ? (
                            <span className="flex items-center gap-1">
                              <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-white" />
                              …
                            </span>
                          ) : doc ? (
                            t("document.regenerate")
                          ) : (
                            t("workspace.documents.generate")
                          )}
                        </button>
                      </div>
                    </article>
                  );
                })()}
              </div>

              {/* Document Previewer Section */}
              <div className="border-t border-slate-100 pt-8 mt-4 space-y-4 animate-in fade-in duration-150">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">
                      {language === "nl" ? "Voorbeeldweergave Documenten" : "Document Previewer"}
                    </h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {language === "nl" ? "Bekijk direct het gegenereerde documentconcept." : "Review the generated draft content below."}
                    </p>
                  </div>
                  
                  {/* Selector tabs for viewer */}
                  <div className="flex rounded-lg bg-slate-100 p-0.5 border border-slate-200/40">
                    <button
                      type="button"
                      onClick={() => setViewerTab("cv")}
                      className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all cursor-pointer focus:outline-none ${
                        viewerTab === "cv"
                          ? "bg-white text-slate-800 shadow-2xs"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {language === "nl" ? "Tailored CV" : "Tailored CV"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewerTab("letter")}
                      className={`rounded-md px-3 py-1.5 text-xs font-bold transition-all cursor-pointer focus:outline-none ${
                        viewerTab === "letter"
                          ? "bg-white text-slate-800 shadow-2xs"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {language === "nl" ? "Cover Letter" : "Cover Letter"}
                    </button>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-5 min-h-[300px] flex flex-col justify-start">
                  {viewerTab === "cv" ? (
                    loadedCvContent ? (
                      <div className="bg-white border border-slate-250 shadow-2xs rounded-lg p-6 max-w-3xl mx-auto w-full text-slate-800 font-sans space-y-6 text-xs leading-relaxed">
                        
                        {/* CV Header */}
                        <div className="border-b border-indigo-100 pb-4 text-center space-y-1">
                          <h1 className="text-xl font-extrabold text-slate-900">{candidateName}</h1>
                          <p className="text-sm font-bold text-indigo-700 uppercase tracking-wide">{loadedCvContent.role_title}</p>
                          <p className="text-[9px] text-slate-400">NL / Dutch Version</p>
                        </div>

                        {/* Profile Summary */}
                        <div className="space-y-1.5">
                          <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Profiel</h4>
                          <p className="text-slate-650 font-medium whitespace-pre-line leading-relaxed">{loadedCvContent.profile_text}</p>
                        </div>

                        {/* Competencies */}
                        {loadedCvContent.competencies && loadedCvContent.competencies.length > 0 && (
                          <div className="space-y-1.5">
                            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Competenties</h4>
                            <div className="flex flex-wrap gap-1.5">
                              {loadedCvContent.competencies.map((c, i) => (
                                <span key={i} className="bg-indigo-50 border border-indigo-100 text-indigo-800 text-[10px] font-bold px-2 py-0.5 rounded">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tech Stack */}
                        {loadedCvContent.tech_stack && Object.keys(loadedCvContent.tech_stack).length > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Tech Stack</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                              {Object.entries(loadedCvContent.tech_stack).map(([category, value]) => (
                                <div key={category} className="space-y-0.5">
                                  <span className="text-[10px] font-bold text-slate-500 uppercase">{category}</span>
                                  <p className="font-semibold text-slate-700">{value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Roles */}
                        {loadedCvContent.roles && loadedCvContent.roles.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Werkervaring</h4>
                            <div className="space-y-4 divide-y divide-slate-100">
                              {loadedCvContent.roles.map((r, i) => (
                                <div key={i} className={`pt-4 first:pt-0 space-y-1.5`}>
                                  <div className="flex items-start justify-between flex-wrap gap-2">
                                    <div>
                                      <h5 className="font-extrabold text-slate-900 text-xs">{r.title}</h5>
                                      <p className="text-[10px] font-semibold text-slate-500">{r.organisation}</p>
                                    </div>
                                    <span className="text-[9px] bg-slate-100 text-slate-650 font-bold px-2 py-0.5 rounded-full">{r.period}</span>
                                  </div>
                                  {r.description && <p className="text-slate-500 italic text-[11px]">{r.description}</p>}
                                  {r.bullets && r.bullets.length > 0 && (
                                    <ul className="list-disc pl-4 space-y-1 text-slate-650 font-medium">
                                      {r.bullets.map((b, bi) => (
                                        <li key={bi}>{b}</li>
                                      ))}
                                    </ul>
                                  )}
                                  {r.result && (
                                    <div className="bg-emerald-50/50 border border-emerald-100 p-2 rounded text-[11px] font-medium text-emerald-800">
                                      <span className="font-bold">Resultaat:</span> {r.result}
                                    </div>
                                  )}
                                  {r.tech && (
                                    <p className="text-[9px] text-slate-400 uppercase tracking-wide font-bold">
                                      Tech: <span className="text-slate-500 font-medium">{r.tech}</span>
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Education */}
                        {loadedCvContent.education && loadedCvContent.education.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Opleiding</h4>
                            <ul className="space-y-1.5 text-slate-650 font-medium">
                              {loadedCvContent.education.map((e, i) => (
                                <li key={i} className="flex justify-between items-center text-xs">
                                  <span><strong className="text-slate-800">{e.degree}</strong>, {e.institution}</span>
                                  <span className="text-slate-400 font-bold">{e.year}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Certifications */}
                        {loadedCvContent.certifications && loadedCvContent.certifications.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Certificaten</h4>
                            <div className="flex flex-wrap gap-1.5">
                              {loadedCvContent.certifications.map((c, i) => (
                                <span key={i} className="bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold px-2 py-0.5 rounded">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Gaps not resolved */}
                        {loadedCvContent.known_gaps_not_resolved && loadedCvContent.known_gaps_not_resolved.length > 0 && (
                          <div className="bg-amber-50/65 border border-amber-100 p-3.5 rounded-lg space-y-1 mt-4">
                            <span className="text-[9px] font-extrabold text-amber-800 uppercase tracking-wider">Openstaande Lacunes (Gaps)</span>
                            <ul className="list-disc pl-4 text-xs text-amber-700 font-medium space-y-0.5">
                              {loadedCvContent.known_gaps_not_resolved.map((g, i) => (
                                <li key={i}>{g}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-400 italic">
                        <p className="text-xs">
                          {language === "nl" ? "Geen Tailored CV gegenereerd." : "No Tailored CV generated yet."}
                        </p>
                      </div>
                    )
                  ) : (
                    loadedLetterContent ? (
                      <div className="bg-white border border-slate-250 shadow-2xs rounded-lg p-8 max-w-2xl mx-auto w-full text-slate-800 font-serif space-y-5 text-xs leading-relaxed">
                        
                        {/* Letter Header */}
                        <div className="font-sans text-right space-y-0.5 pb-2 text-[10px] text-slate-400 font-medium">
                          <p>{candidateName}</p>
                          <p>{new Date(workflow.documents?.motivatiebrief?.generated_at || new Date()).toLocaleDateString()}</p>
                        </div>

                        {/* Letter Salutation */}
                        <p className="font-bold text-slate-900">{loadedLetterContent.salutation}</p>

                        {/* Opening */}
                        <p className="whitespace-pre-line text-slate-700">{loadedLetterContent.opening_paragraph}</p>

                        {/* Body Paragraphs */}
                        {loadedLetterContent.body_paragraphs && loadedLetterContent.body_paragraphs.map((para, i) => (
                          <p key={i} className="whitespace-pre-line text-slate-700">{para}</p>
                        ))}

                        {/* Closing */}
                        <p className="whitespace-pre-line text-slate-700">{loadedLetterContent.closing_paragraph}</p>

                        {/* Sign-off */}
                        <div className="pt-4 space-y-4 font-sans text-xs">
                          <p className="text-slate-650 font-medium">{loadedLetterContent.sign_off}</p>
                          <div className="space-y-0.5">
                            <p className="font-extrabold text-slate-900">{candidateName}</p>
                            <p className="text-[10px] font-semibold text-slate-400 font-sans">Adapt Interim Professionals</p>
                          </div>
                        </div>

                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-400 italic">
                        <p className="text-xs">
                          {language === "nl" ? "Geen begeleidende brief (Cover Letter) gegenereerd." : "No Cover Letter generated yet."}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>

            </div>
          )}

        </div>
      )}

    </div>
  );
}
